import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardData, getKnowledge, getKnowledgeChapter, getAffiliates, searchKnowledge, createReview, signContract } from '../api';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getSettings } from '../api';
import { toast } from 'sonner';
import {
  BookOpen, ChevronRight, Lock, Calendar, CreditCard,
  FileText, Cat, Heart, Clock, ArrowLeft, CheckCircle,
  Home as HomeIcon, Snowflake, Star, Shield, Sparkles,
  Stethoscope, Scissors, TreePine, Baby, Users, Truck, Scale,
  PawPrint, ShoppingBag, Droplets, Search, Sofa
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Knowledge chapter icons
const chapterIcons = [Cat, HomeIcon, Droplets, Scissors, Stethoscope, TreePine, Shield, Baby, Sparkles, Users, Star, Truck, Scale, CreditCard, Heart, ShoppingBag];
const chapterColors = ['#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E', '#7A9BB5', '#C9A96E'];

const BuyerDashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [chapters, setChapters] = useState([]);
  const [activeChapter, setActiveChapter] = useState(null);
  const [chapterContent, setChapterContent] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    Promise.all([getDashboardData(), getSettings(), getKnowledge()])
      .then(([dashRes, settingsRes, knowledgeRes]) => {
        setData(dashRes.data);
        setSettings(settingsRes.data);
        setChapters(knowledgeRes.data);
      })
      .catch((err) => { console.error(err); })
      .finally(() => setLoading(false));
  }, []);

  const loadChapter = async (key) => {
    try { const res = await getKnowledgeChapter(key); setChapterContent(res.data); setActiveChapter(key); }
    catch (err) { toast.error('Kapitel konnte nicht geladen werden'); }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) { setSearchResults(null); return; }
    try { const res = await searchKnowledge(q); setSearchResults(res.data); }
    catch (err) { setSearchResults([]); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F8]">
        <Snowflake size={48} className="text-[#7A9BB5] animate-spin" style={{ animationDuration: '3s' }} />
      </div>
    );
  }

  const isAdmin = data?.is_admin;
  const hasDeposit = data?.has_deposit || isAdmin;
  const buyer = data?.buyer;
  const litters = data?.litters || [];
  const contracts = data?.contracts || [];
  const affiliates = data?.affiliates || [];
  const exclusiveMedia = data?.exclusive_media || [];
  const assignedKitten = data?.assigned_kitten;
  const assignedLitter = data?.assigned_litter;

  let daysUntilPickup = null;
  if (buyer?.pickup_date) {
    const pickup = new Date(buyer.pickup_date);
    daysUntilPickup = Math.max(0, Math.ceil((pickup - new Date()) / (1000 * 60 * 60 * 24)));
  }

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: HomeIcon },
    { id: 'knowledge', label: 'Wissensportal', icon: BookOpen },
  ];
  if (hasDeposit || isAdmin || data?.can_see_litters) tabs.push({ id: 'litters', label: 'Würfe & Kitten', icon: Cat });
  if (exclusiveMedia.length > 0 || assignedKitten) tabs.push({ id: 'mykitten', label: 'Mein Kitten', icon: Heart });
  if (hasDeposit || isAdmin) tabs.push({ id: 'contracts', label: 'Verträge', icon: FileText });
  if (affiliates.length > 0 || isAdmin) tabs.push({ id: 'recommendations', label: 'Empfehlungen', icon: Heart });
  if (!isAdmin) tabs.push({ id: 'review', label: 'Bewertung', icon: Star });

  return (
    <div className="min-h-screen bg-[#EEF3F8]">
      <Navigation user={user} onLogout={onLogout} settings={settings} isLanding={false} />

      {/* Banner Image */}
      <div className="relative w-full h-32 md:h-44 overflow-hidden -z-0">
        <img
          src={settings.banner_dashboard_url ? `${BACKEND_URL}${settings.banner_dashboard_url}` : `${window.location.origin}/banner.jpg`}
          alt="Birma von den Schneeblumen"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#2E3A46]"></div>
      </div>

      {/* Hero header */}
      <div className="bg-[#2E3A46] text-white pb-6 md:pb-8 -mt-1 relative z-0">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          {isAdmin && (
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#7A9BB5] hover:text-white text-sm mb-2">
              <ArrowLeft size={14} /> Zurück zum Admin-Panel
            </Link>
          )}
          <h1 className="font-heading text-2xl md:text-3xl mb-2">
            {isAdmin ? 'Dashboard-Vorschau' : `Hallo ${data?.user?.full_name?.split(' ')[0] || ''}, willkommen bei den Schneeblumen!`}
          </h1>
          <p className="text-[#C5D0DA] text-sm md:text-base max-w-2xl">
            {isAdmin ? 'So sehen Ihre Interessenten und Käufer das Dashboard.' : 'Schön, dass Sie hier sind! In Ihrem persönlichen Bereich finden Sie alles Wichtige rund um Ihre Birma – vom Wissensportal über aktuelle Würfe bis hin zu exklusiven Fotos und Empfehlungen.'}
          </p>
          {/* Quick stats */}
          <div className="flex flex-wrap gap-3 mt-6">
            <div className="bg-[#354551] rounded-xl px-4 py-2.5 flex items-center gap-2">
              <BookOpen size={16} className="text-[#7A9BB5]" />
              <span className="text-sm">{chapters.length} Wissenskapitel</span>
            </div>
            {litters.length > 0 && (
              <div className="bg-[#354551] rounded-xl px-4 py-2.5 flex items-center gap-2">
                <Cat size={16} className="text-[#C9A96E]" />
                <span className="text-sm">{litters.length} Würfe</span>
              </div>
            )}
            {daysUntilPickup !== null && (
              <div className="bg-[#C9A96E]/20 border border-[#C9A96E]/30 rounded-xl px-4 py-2.5 flex items-center gap-2">
                <Clock size={16} className="text-[#C9A96E]" />
                <span className="text-sm font-medium text-[#C9A96E]">{daysUntilPickup} Tage bis zum Einzug</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-6 md:py-10">
        {/* Tabs */}
        <div className="flex overflow-x-auto gap-2 mb-8 pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setActiveChapter(null); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                  activeTab === tab.id ? 'bg-[#2E3A46] text-white shadow-md' : 'bg-white text-[#2E3A46] border border-[#C5D0DA] hover:bg-[#D8E8F0]'
                }`}>
                <Icon size={16} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* === OVERVIEW === */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-fade-in">
            {/* Welcome card */}
            <div className="card-frost overflow-hidden">
              <div className="bg-gradient-to-r from-[#2E3A46] to-[#354551] p-6 md:p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#7A9BB5]/20 flex items-center justify-center shrink-0">
                    <Snowflake size={28} className="text-[#7A9BB5]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl mb-2">
                      {isAdmin ? 'Dashboard-Vorschau' : `Willkommen bei den Schneeblumen, ${data?.user?.full_name?.split(' ')[0] || ''}!`}
                    </h2>
                    <p className="text-[#C5D0DA] text-sm leading-relaxed">
                      Hier finden Sie alles Wichtige rund um Ihre Birma – von umfangreichem Wissen über die Rasse, persönlichen Empfehlungen bis hin zu exklusiven Fotos und Verträgen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preparation Guide Card */}
            <div className="card-frost p-5 border-l-4 border-[#7A9BB5] hover:shadow-lg transition-shadow cursor-pointer" onClick={() => { setActiveTab('knowledge'); }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#D8E8F0] flex items-center justify-center shrink-0">
                  <Sofa size={22} className="text-[#7A9BB5]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-base text-[#2E3A46] mb-1">Ihr Zuhause vorbereiten</h3>
                  <p className="text-xs text-[#7A9BB5]">Kratzbäume, Futternäpfe, Futter, Eingewöhnung – alles was Sie wissen müssen, bevor Ihre Birma einzieht.</p>
                </div>
                <ChevronRight size={18} className="text-[#C5D0DA] shrink-0" />
              </div>
            </div>

            {/* Quick access cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button onClick={() => setActiveTab('knowledge')} className="card-frost p-5 text-left hover:shadow-lg transition-shadow group">
                <div className="w-12 h-12 rounded-xl bg-[#D8E8F0] flex items-center justify-center mb-3 group-hover:bg-[#7A9BB5] transition-colors">
                  <BookOpen size={22} className="text-[#7A9BB5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-lg text-[#2E3A46] mb-1">Wissensportal</h3>
                <p className="text-sm text-[#7A9BB5]">{chapters.length} Kapitel rund um die Birmahaltung</p>
                <span className="text-sm text-[#7A9BB5] flex items-center gap-1 mt-3 group-hover:text-[#2E3A46]">Kapitel lesen <ChevronRight size={14} /></span>
              </button>

              {(hasDeposit || data?.can_see_litters) && (
                <button onClick={() => setActiveTab('litters')} className="card-frost p-5 text-left hover:shadow-lg transition-shadow group">
                  <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/15 flex items-center justify-center mb-3 group-hover:bg-[#C9A96E] transition-colors">
                    <Cat size={22} className="text-[#C9A96E] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg text-[#2E3A46] mb-1">Würfe & Kitten</h3>
                  <p className="text-sm text-[#7A9BB5]">{litters.length > 0 ? `${litters.length} aktive Würfe` : 'Aktuell keine Würfe'}</p>
                  <span className="text-sm text-[#7A9BB5] flex items-center gap-1 mt-3 group-hover:text-[#2E3A46]">Ansehen <ChevronRight size={14} /></span>
                </button>
              )}

              {affiliates.length > 0 && (
                <button onClick={() => setActiveTab('recommendations')} className="card-frost p-5 text-left hover:shadow-lg transition-shadow group">
                  <div className="w-12 h-12 rounded-xl bg-[#D8E8F0] flex items-center justify-center mb-3 group-hover:bg-[#7A9BB5] transition-colors">
                    <Heart size={22} className="text-[#7A9BB5] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg text-[#2E3A46] mb-1">Empfehlungen</h3>
                  <p className="text-sm text-[#7A9BB5]">{affiliates.length} Produktempfehlungen</p>
                  <span className="text-sm text-[#7A9BB5] flex items-center gap-1 mt-3 group-hover:text-[#2E3A46]">Entdecken <ChevronRight size={14} /></span>
                </button>
              )}

              {(assignedKitten || exclusiveMedia.length > 0) && (
                <button onClick={() => setActiveTab('mykitten')} className="card-frost p-5 text-left hover:shadow-lg transition-shadow group border-l-4 border-[#C9A96E]">
                  <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/15 flex items-center justify-center mb-3 group-hover:bg-[#C9A96E] transition-colors">
                    <Heart size={22} className="text-[#C9A96E] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg text-[#2E3A46] mb-1">{assignedKitten ? assignedKitten.name : 'Mein Kitten'}</h3>
                  <p className="text-sm text-[#7A9BB5]">
                    {exclusiveMedia.length > 0 ? `${exclusiveMedia.length} exklusive Medien` : 'Persönliche Infos'}
                  </p>
                  <span className="text-sm text-[#C9A96E] flex items-center gap-1 mt-3">Ansehen <ChevronRight size={14} /></span>
                </button>
              )}

              {!hasDeposit && !isAdmin && (
                <div className="card-frost p-5 border border-dashed border-[#C5D0DA]">
                  <div className="w-12 h-12 rounded-xl bg-[#C5D0DA]/20 flex items-center justify-center mb-3">
                    <Lock size={22} className="text-[#C5D0DA]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#2E3A46] mb-1">Erweiterte Inhalte</h3>
                  <p className="text-sm text-[#C5D0DA]">Nach Anzahlung freigeschaltet: Kitten-Details, Verträge & mehr</p>
                </div>
              )}
            </div>

            {/* Deposit info */}
            {hasDeposit && buyer && !isAdmin && (
              <div className="card-frost p-6 border-l-4 border-[#C9A96E]">
                <h3 className="font-heading text-lg text-[#2E3A46] mb-3">Zahlungsübersicht</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-green-500 shrink-0" />
                    <div><p className="text-xs text-[#7A9BB5]">Anzahlung</p><p className="text-sm font-medium text-[#2E3A46]">Erhalten {buyer.deposit_date || ''}</p></div>
                  </div>
                  {buyer.pickup_date && (
                    <div className="flex items-center gap-3">
                      <Calendar size={20} className="text-[#7A9BB5] shrink-0" />
                      <div><p className="text-xs text-[#7A9BB5]">Einzugstermin</p><p className="text-sm font-medium text-[#2E3A46]">{buyer.pickup_date}</p></div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === KNOWLEDGE PORTAL === */}
        {activeTab === 'knowledge' && (
          <div className="animate-fade-in">
            {activeChapter && chapterContent ? (
              <div>
                <button onClick={() => { setActiveChapter(null); setChapterContent(null); }} className="flex items-center gap-2 text-[#7A9BB5] hover:text-[#2E3A46] text-sm mb-6">
                  <ArrowLeft size={16} /> Zurück zur Übersicht
                </button>
                <div className="card-frost overflow-hidden">
                  <div className="bg-gradient-to-r from-[#2E3A46] to-[#354551] p-6 md:p-8">
                    <h2 className="font-heading text-2xl md:text-3xl text-white">{chapterContent.title}</h2>
                  </div>
                  {/* Images above text */}
                  {chapterContent.images?.length > 0 && chapterContent.image_position === 'top' && (
                    <div className={chapterContent.images.length === 1 ? '' : 'grid grid-cols-2 gap-1'}>
                      {chapterContent.images.map((url, i) => (
                        <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-full h-48 md:h-64 object-cover" />
                      ))}
                    </div>
                  )}
                  <div className="p-6 md:p-10">
                    {/* Images left/right of text */}
                    {chapterContent.images?.length > 0 && (chapterContent.image_position === 'left' || chapterContent.image_position === 'right') ? (
                      <div className={`flex flex-col md:flex-row gap-6 ${chapterContent.image_position === 'right' ? 'md:flex-row-reverse' : ''}`}>
                        <div className="md:w-2/5 shrink-0">
                          <div className="space-y-2">
                            {chapterContent.images.map((url, i) => (
                              <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-full rounded-xl object-cover" />
                            ))}
                          </div>
                        </div>
                        <div className="flex-1 prose max-w-none text-[#354551] leading-relaxed whitespace-pre-line text-base">
                          {chapterContent.body}
                        </div>
                      </div>
                    ) : (
                      <div className="prose max-w-none text-[#354551] leading-relaxed whitespace-pre-line text-base">
                        {chapterContent.body}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-[#2E3A46] flex items-center justify-center mx-auto mb-4">
                    <BookOpen size={28} className="text-[#7A9BB5]" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl text-[#2E3A46] mb-2">Wissensportal</h2>
                  <p className="text-[#7A9BB5] max-w-lg mx-auto">Alles, was Sie über die Haltung, Pflege und Gesundheit Ihrer Heiligen Birma wissen müssen.</p>
                  {/* Search Field */}
                  <div className="relative max-w-md mx-auto mt-6">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C5D0DA]" />
                    <input
                      type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Kapitel durchsuchen... z.B. Futter, Impfung, Pflege"
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] text-sm focus:border-[#7A9BB5] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(searchResults || chapters).map((chapter, idx) => {
                    const Icon = chapterIcons[idx] || BookOpen;
                    const color = chapterColors[idx] || '#7A9BB5';
                    return (
                      <button key={chapter.key} onClick={() => loadChapter(chapter.key)}
                        className="card-frost p-5 text-left hover:shadow-lg transition-all group hover:-translate-y-0.5">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
                            <Icon size={20} style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-bold text-[#C5D0DA]">{String(idx + 1).padStart(2, '0')}</span>
                              <h3 className="font-medium text-[#2E3A46] text-sm group-hover:text-[#7A9BB5] transition-colors truncate">
                                {chapter.title}
                              </h3>
                            </div>
                            <p className="text-xs text-[#7A9BB5] line-clamp-2">{chapter.body?.substring(0, 80)}...</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === LITTERS === */}
        {activeTab === 'litters' && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl text-[#2E3A46] mb-2">Würfe & Kitten</h2>
              <p className="text-[#7A9BB5]">Aktuelle Informationen zu unseren Würfen</p>
            </div>
            {litters.length > 0 ? (
              <div className="space-y-6">
                {litters.map((litter) => (
                  <div key={litter.id} className="card-frost overflow-hidden">
                    <div className="bg-[#D8E8F0] px-6 py-4 border-b border-[#C5D0DA]/50">
                      <h3 className="font-heading text-xl text-[#2E3A46]">{litter.name}</h3>
                      {litter.born_at && <p className="text-sm text-[#7A9BB5] mt-1">Geboren: {litter.born_at}</p>}
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        {litter.mother && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#EEF3F8]">
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#D8E8F0] shrink-0">
                              {litter.mother.photos?.length > 0 ? <img src={`${BACKEND_URL}${litter.mother.photos[0]}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Cat size={20} className="text-[#C5D0DA]" /></div>}
                            </div>
                            <div><p className="text-xs text-[#7A9BB5]">Mutter</p><p className="text-sm font-medium text-[#2E3A46]">{litter.mother.name}</p>{litter.mother.base_color && <p className="text-xs text-[#7A9BB5]">{litter.mother.base_color}</p>}</div>
                          </div>
                        )}
                        {litter.father && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#EEF3F8]">
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#D8E8F0] shrink-0">
                              {litter.father.photos?.length > 0 ? <img src={`${BACKEND_URL}${litter.father.photos[0]}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Cat size={20} className="text-[#C5D0DA]" /></div>}
                            </div>
                            <div><p className="text-xs text-[#7A9BB5]">Vater</p><p className="text-sm font-medium text-[#2E3A46]">{litter.father.name}</p>{litter.father.base_color && <p className="text-xs text-[#7A9BB5]">{litter.father.base_color}</p>}</div>
                          </div>
                        )}
                      </div>
                      {litter.kittens?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {litter.kittens.map(k => (
                            <div key={k.id} className="p-3 rounded-xl border border-[#C5D0DA] bg-white flex items-center justify-between">
                              <div>
                                <span className="font-medium text-sm text-[#2E3A46]">{k.name || 'Unbenannt'}</span>
                                {k.color && <p className="text-xs text-[#7A9BB5]">{k.color}</p>}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${k.status === 'available' ? 'bg-green-100 text-green-700' : k.status === 'reserved' ? 'bg-[#C9A96E]/15 text-[#C9A96E]' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>
                                {k.status === 'available' ? 'Verfügbar' : k.status === 'reserved' ? 'Reserviert' : 'Vergeben'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card-frost p-12 text-center">
                <Cat size={48} className="text-[#C5D0DA] mx-auto mb-4" />
                <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Aktuell keine Würfe</h3>
                <p className="text-sm text-[#7A9BB5]">Wir informieren Sie, sobald es Neuigkeiten gibt.</p>
              </div>
            )}
          </div>
        )}

        {/* === MEIN KITTEN + EXKLUSIVE MEDIEN === */}
        {activeTab === 'mykitten' && (
          <div className="animate-fade-in space-y-6">
            {assignedKitten && (
              <div className="card-frost overflow-hidden">
                <div className="bg-gradient-to-r from-[#C9A96E] to-[#b8943f] p-5 text-white">
                  <h2 className="font-heading text-xl">Ihr Schneeblumen-Kitten</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-heading text-2xl text-[#2E3A46] mb-2">{assignedKitten.name || 'Name folgt'}</h3>
                      {assignedKitten.color && <p className="text-sm text-[#7A9BB5] mb-1">Farbe: {assignedKitten.color}</p>}
                      {assignedKitten.gender && <p className="text-sm text-[#7A9BB5] mb-3">Geschlecht: {assignedKitten.gender === 'male' ? 'Männlich' : 'Weiblich'}</p>}
                      {assignedKitten.character_text && <p className="text-[#354551] leading-relaxed">{assignedKitten.character_text}</p>}
                    </div>
                    {assignedLitter && (
                      <div className="space-y-3">
                        <h4 className="font-heading text-lg text-[#2E3A46]">Wurf: {assignedLitter.name}</h4>
                        {assignedLitter.mother && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#EEF3F8]">
                            <Cat size={18} className="text-[#7A9BB5]" />
                            <div><p className="text-xs text-[#7A9BB5]">Mutter</p><p className="text-sm font-medium text-[#2E3A46]">{assignedLitter.mother.name}</p></div>
                          </div>
                        )}
                        {assignedLitter.father && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#EEF3F8]">
                            <Cat size={18} className="text-[#7A9BB5]" />
                            <div><p className="text-xs text-[#7A9BB5]">Vater</p><p className="text-sm font-medium text-[#2E3A46]">{assignedLitter.father.name}</p></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Exclusive Media */}
            {exclusiveMedia.length > 0 && (
              <div>
                <h3 className="font-heading text-xl text-[#2E3A46] mb-2">Ihre exklusiven Fotos & Videos</h3>
                <p className="text-sm text-[#7A9BB5] mb-4">Diese Medien wurden speziell für Sie bereitgestellt.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {exclusiveMedia.map(m => (
                    <div key={m.id} className="card-frost overflow-hidden">
                      <div className="aspect-square">
                        {m.type === 'video' ? (
                          <video src={`${BACKEND_URL}${m.url}`} controls className="w-full h-full object-cover" />
                        ) : (
                          <img src={`${BACKEND_URL}${m.url}`} alt={m.caption || 'Exklusiv'} className="w-full h-full object-cover" />
                        )}
                      </div>
                      {m.caption && <p className="text-xs text-[#354551] p-2">{m.caption}</p>}
                      <p className="text-[10px] text-[#C5D0DA] px-2 pb-2">{new Date(m.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!assignedKitten && exclusiveMedia.length === 0 && (
              <div className="card-frost p-12 text-center">
                <Heart size={48} className="text-[#C5D0DA] mx-auto mb-4" />
                <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Ihr persönlicher Bereich</h3>
                <p className="text-sm text-[#7A9BB5]">Hier werden bald Informationen zu Ihrem Kitten und exklusive Fotos erscheinen.</p>
              </div>
            )}
          </div>
        )}

        {/* === CONTRACTS === */}
        {activeTab === 'contracts' && (
          <ContractsTab contracts={contracts} isAdmin={isAdmin} />
        )}

        {/* === RECOMMENDATIONS === */}
        {activeTab === 'recommendations' && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl text-[#2E3A46] mb-2">Unsere Empfehlungen</h2>
              <p className="text-[#7A9BB5]">Produkte die wir aus eigener Erfahrung empfehlen</p>
            </div>
            {affiliates.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {affiliates.map(aff => {
                  const catLabels = { wasserfilter: 'Wasserfilter', nassfutter: 'Nassfutter', trockenfutter: 'Trockenfutter', kratzbaum: 'Kratzbäume', spielzeug: 'Spielzeug', pflegeprodukt: 'Pflegeprodukte', transportbox: 'Transportboxen', streu: 'Streu', sicherheitsnetz: 'Sicherheitsnetze' };
                  return (
                    <a key={aff.id} href={aff.affiliate_url || '#'} target="_blank" rel="noopener noreferrer"
                      className="card-frost p-5 hover:shadow-lg transition-shadow relative group">
                      <span className="absolute top-3 right-3 text-[10px] px-1.5 py-0.5 rounded bg-[#2E3A46]/70 text-white">Werbung</span>
                      <div className="w-10 h-10 rounded-xl bg-[#D8E8F0] flex items-center justify-center mb-3">
                        <ShoppingBag size={18} className="text-[#7A9BB5]" />
                      </div>
                      <h3 className="font-medium text-[#2E3A46] mb-1 group-hover:text-[#7A9BB5] pr-12">{aff.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#D8E8F0] text-[#7A9BB5] inline-block mb-2">{catLabels[aff.category] || aff.category}</span>
                      {aff.description && <p className="text-sm text-[#354551] line-clamp-2">{aff.description}</p>}
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="card-frost p-12 text-center">
                <Heart size={48} className="text-[#C5D0DA] mx-auto mb-4" />
                <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Empfehlungen folgen</h3>
                <p className="text-sm text-[#7A9BB5]">Hier werden bald Produktempfehlungen angezeigt.</p>
              </div>
            )}
          </div>
        )}
        {/* === REVIEW === */}
        {activeTab === 'review' && !isAdmin && (
          <ReviewForm userName={data?.user?.full_name || ''} />
        )}
      </div>

      <Footer settings={settings} />
    </div>
  );
};

const ContractsTab = ({ contracts, isAdmin }) => {
  const [signingId, setSigningId] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [sigAccept, setSigAccept] = useState(false);
  const canvasRef = React.useRef(null);
  const sigPadRef = React.useRef(null);
  const [signing, setSigning] = useState(false);

  React.useEffect(() => {
    if (signingId && canvasRef.current && !sigPadRef.current) {
      import('signature_pad').then(({ default: SignaturePad }) => {
        sigPadRef.current = new SignaturePad(canvasRef.current, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: '#2E3A46',
        });
      });
    }
    return () => { if (sigPadRef.current) { sigPadRef.current.off(); sigPadRef.current = null; } };
  }, [signingId]);

  const handleSign = async () => {
    if (!signedName) { toast.error('Bitte Name eingeben'); return; }
    if (!sigAccept) { toast.error('Bitte bestätigen Sie den Vertrag'); return; }
    setSigning(true);
    try {
      const sigData = sigPadRef.current && !sigPadRef.current.isEmpty() ? sigPadRef.current.toDataURL() : '';
      await signContract(signingId, { signed_name: signedName, signature_data: sigData });
      toast.success('Vertrag unterschrieben!');
      setSigningId(null);
      window.location.reload();
    } catch (err) { toast.error('Fehler beim Unterschreiben'); }
    finally { setSigning(false); }
  };

  return (
    <div className="animate-fade-in">
      <h2 className="font-heading text-xl text-[#2E3A46] mb-4">{isAdmin ? 'Alle Verträge' : 'Ihre Verträge'}</h2>
      {contracts.length > 0 ? (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <div key={contract.id} className="card-frost p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-[#2E3A46]">
                    {contract.type === 'purchase' ? 'Kaufvertrag' : contract.type === 'deposit' ? 'Anzahlungsbestätigung' : contract.type === 'breeding_ban' ? 'Zuchtverbot' : 'Fotoeinwilligung'}
                  </h3>
                  <p className="text-xs text-[#7A9BB5] mt-1">{new Date(contract.created_at).toLocaleDateString('de-DE')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {contract.status === 'approved' && !isAdmin && (
                    <button onClick={() => setSigningId(contract.id)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1">
                      <FileText size={12} /> Jetzt unterschreiben
                    </button>
                  )}
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${contract.status === 'signed' ? 'bg-green-100 text-green-700' : contract.status === 'approved' ? 'bg-[#C9A96E]/15 text-[#C9A96E]' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>
                    {contract.status === 'signed' ? 'Unterschrieben' : contract.status === 'approved' ? 'Bereit' : 'Entwurf'}
                  </span>
                </div>
              </div>

              {/* Signature Dialog */}
              {signingId === contract.id && (
                <div className="mt-4 pt-4 border-t border-[#C5D0DA]/50 animate-fade-in">
                  <h4 className="font-heading text-lg text-[#2E3A46] mb-3">Vertrag unterschreiben</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-[#2E3A46] mb-1">Ihr vollständiger Name</label>
                      <input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none"
                        placeholder="Vor- und Nachname" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2E3A46] mb-2">Unterschrift (zeichnen Sie mit Maus oder Finger)</label>
                      <div className="border-2 border-[#C5D0DA] rounded-xl overflow-hidden bg-white">
                        <canvas ref={canvasRef} width={500} height={200} className="w-full touch-none" style={{height: '150px'}} />
                      </div>
                      <button onClick={() => sigPadRef.current?.clear()} className="text-xs text-[#7A9BB5] hover:text-[#2E3A46] mt-1">Unterschrift löschen</button>
                    </div>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={sigAccept} onChange={(e) => setSigAccept(e.target.checked)} className="mt-1 w-4 h-4 accent-[#7A9BB5]" />
                      <span className="text-[#354551]">Ich habe den Vertrag gelesen und bin mit allen Vereinbarungen einverstanden.</span>
                    </label>
                    <div className="flex gap-3">
                      <button onClick={handleSign} disabled={signing} className="btn-primary flex items-center gap-2">
                        {signing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Verbindlich unterschreiben'}
                      </button>
                      <button onClick={() => setSigningId(null)} className="btn-outline">Abbrechen</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card-frost p-12 text-center">
          <FileText size={48} className="text-[#C5D0DA] mx-auto mb-4" />
          <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Noch keine Verträge</h3>
          <p className="text-sm text-[#7A9BB5]">Verträge werden hier bereitgestellt.</p>
        </div>
      )}
    </div>
  );
};

const ReviewForm = ({ userName }) => {
  const [form, setForm] = useState({ name: userName, rating: 5, text: '' });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.text) { toast.error('Bitte schreiben Sie einen Bewertungstext'); return; }
    setSaving(true);
    try {
      await createReview(form);
      toast.success('Vielen Dank für Ihre Bewertung! Sie wird nach Prüfung veröffentlicht.');
      setSubmitted(true);
    } catch (err) { toast.error('Fehler beim Senden'); }
    finally { setSaving(false); }
  };

  if (submitted) {
    return (
      <div className="card-frost p-10 text-center animate-fade-in">
        <Star size={48} className="text-[#C9A96E] mx-auto mb-4" />
        <h3 className="font-heading text-xl text-[#2E3A46] mb-2">Vielen Dank!</h3>
        <p className="text-sm text-[#7A9BB5]">Ihre Bewertung wurde eingereicht und wird nach Prüfung veröffentlicht.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl text-[#2E3A46] mb-2">Bewertung schreiben</h2>
        <p className="text-sm text-[#7A9BB5]">Teilen Sie Ihre Erfahrungen mit den Schneeblumen</p>
      </div>
      <div className="card-frost p-6 max-w-lg mx-auto">
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Ihr Name</label>
          <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Bewertung</label>
          <div className="flex gap-1">
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setForm(f => ({ ...f, rating: s }))} className="p-1">
                <Star size={28} className={s <= form.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
              </button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Ihre Erfahrung</label>
          <textarea value={form.text} onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))} rows={4}
            className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none"
            placeholder="Erzählen Sie von Ihrer Erfahrung mit den Schneeblumen..." />
        </div>
        <button onClick={handleSubmit} disabled={saving}
          className="w-full btn-primary flex items-center justify-center gap-2 py-3">
          {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><Star size={16} /> Bewertung abschicken</>}
        </button>
      </div>
    </div>
  );
};

export default BuyerDashboard;
